import type { IOrderAddress, IShippingRequestAddress } from '@rogue/universal'
import { getShippingZone } from '@/js/helpers/validations/shipping-zones'
import { getBuyRequestMeta } from '@/js/helpers/buy-request'
import { missingBumperSetBreakdown } from '@/js/helpers/bumper-set'
import type { BuyRequest } from '@/js/services/types/order'
import type { IRogue, Product } from '@/js/services/RogueTypes'

 
enum MessageType {
  ERROR = 'ERROR', INFO = 'INFO',
}

type Message = {
  text: string,
  disable: boolean,
  type: MessageType,
}

type ValidateFn = (buyRequests:BuyRequest[], products: {[key: string]: Product}, shippingAddress?: IShippingRequestAddress | IOrderAddress, rogue?: IRogue) => Message | undefined

const excludedFilterWords = ['God', 'Balls']

export const validateGiftCardCustomAmount: ValidateFn = function (buyRequests: BuyRequest[]): Message | undefined {
  const MINIMUM_CUSTOM_AMOUNT = 25

  const MESSAGE = 'Gift card custom amounts cannot be less than $25.00'
  // filter out non-custom products
  const buyRequestGiftCards = buyRequests.filter(br => br.metadata?.giftcard && br.metadata?.giftcard.customAmount && br.qty)
  if (!buyRequestGiftCards.length) {
    return undefined
  }

  const invalidBuyRequest = buyRequestGiftCards.find(buyRequest => buyRequest.metadata?.giftcard
      && buyRequest.metadata?.giftcard.customAmount
      && buyRequest.metadata?.giftcard.customAmount < MINIMUM_CUSTOM_AMOUNT)

  if (invalidBuyRequest) {
    return { text: MESSAGE, disable: true, type: MessageType.ERROR }
  }

  return undefined
}

export const validateMinimumWeight: ValidateFn = (buyRequests: BuyRequest[], products: {[key: string]: Product}): Message | undefined => {
  const MINIMUM_WEIGHT = 200

  const MESSAGE = 'You must order a minimum of 200lbs per bumper'
  // filter out non-custom products
  const customProductBuyRequests = buyRequests.filter(br => br.metadata?.nonFluidCustomization && br.qty)
  if (!customProductBuyRequests.length) {
    return undefined
  }

  const recipesWeights: {[key: string]: number} = {}
  // loop through custom buy requests and add up weight per recipeId
  customProductBuyRequests.forEach((buyRequest) => {
    const product = products[buyRequest.productId]
    const recipeId = buyRequest.metadata?.recipeHash
    if (recipeId && product) {
      let weight = recipesWeights[recipeId] || 0
      weight += (product.weight || 0) * buyRequest.qty
      recipesWeights[recipeId] = weight
    }
  })

  const invalidRecipe = Object.keys(recipesWeights).find(recipeId => recipesWeights[recipeId] < MINIMUM_WEIGHT)

  if (invalidRecipe) {
    return { text: MESSAGE, disable: true, type: MessageType.ERROR }
  }

  return undefined
}

export const validateBumperSetBreakdown: ValidateFn = (buyRequests: BuyRequest[], products: {[key: string]: Product}): Message | undefined => {
  const MESSAGE = 'Please choose the plate breakdown for your bumper set. Use the Edit link on the item to pick your plates.'

  const invalid = buyRequests.filter(br => br.qty && missingBumperSetBreakdown(br, products))

  if (invalid.length) {
    return { text: MESSAGE, disable: true, type: MessageType.ERROR }
  }

  return undefined
}

export const validateShippingZone = (buyRequests: BuyRequest[], products: {[key: string]: Product}, shippingAddress?: IShippingRequestAddress | IOrderAddress, rogue?: IRogue): Message | undefined => {
  if (!shippingAddress || !shippingAddress.region) {
    return undefined
  }
  const correctShippingZone = getShippingZone(shippingAddress.region!)
  const metas = buyRequests.map(buyRequest => getBuyRequestMeta(buyRequest, rogue!))
  const invalid = metas.filter(brm => brm.customOptions && (brm.customOptions!['Choose a shipping zone'] !== correctShippingZone))
  if (invalid.length) {
    return { text: `${shippingAddress.region} is outside of the shipping zone you chose. Please update to the ${correctShippingZone} shipping zone.`, disable: true, type: MessageType.ERROR }
  }

  return undefined
}

export const validateBadWords = async (text: string) => {
  try {
    const { Filter } = await import(/* webpackChunkName: "badWords" */'bad-words')
    const filter = new Filter()
    filter.removeWords(...excludedFilterWords)

    return filter.isProfane(text)
  } catch (e) {
    return false
  }
}

const errors: ValidateFn[] = [
  validateMinimumWeight,
  validateGiftCardCustomAmount,
  validateBumperSetBreakdown,
  // validateShippingZone,
]

const runMethods = (methods: ValidateFn[], buyRequests: BuyRequest[], products: {[key: string]: Product}, shippingAddress?: IShippingRequestAddress | IOrderAddress, rogue?: IRogue): Message[] => {
  const results = methods.map((method) => method(buyRequests, products, shippingAddress, rogue))

  return results.filter((message): message is Message => message !== undefined)
}
export default (buyRequests: BuyRequest[], products: {[key: string]: Product}, shippingAddress?: IShippingRequestAddress | IOrderAddress, rogue?: IRogue): Message[] => runMethods(errors, buyRequests, products, shippingAddress, rogue)
